import { gql } from '@apollo/client';

/**
 * Unified bootstrap query for app initialization.
 *
 * Returns all data needed to initialize the application in a single request:
 * - User's companies list
 * - Selected company details
 * - Onboarding state
 * - Subscription status
 * - Shopify permission status
 * - Company settings
 *
 * This replaces the sequential calls to:
 * - GET_COMPANIES_FOR_USER
 * - GET_ONBOARDING_STATE
 * - GET_COMPANY_SETTINGS
 * - GET_SHOPIFY_PERMISSION_STATUS
 * - /validate-subscription endpoint
 */
export const GET_APP_BOOTSTRAP = gql`
  query GetAppBootstrap($companyId: String) {
    getAppBootstrap(companyId: $companyId) {
      companies {
        id
        companyName
        bootstrapProgress {
          phase
          cursor
          total
          ratio
          percentage
          isReady
        }
      }
      selectedCompany {
        id
        companyName
        bootstrapProgress {
          phase
          cursor
          total
          ratio
          percentage
          isReady
        }
      }
      onboardingState {
        isNew
        currentStep
        completedSteps
        billingPlan
      }
      subscriptionValid
      shopifyPermissionStatus {
        status
        message
        requiresReapproval
        stores {
          storeId
          storeDomain
          requiresReapproval
          requiredScopes
        }
      }
      currencyCode
      defaultSafetyStockWeeks
      fallbackSupplierDeliveryTime
      warningThreshold
      preferredMetric
    }
  }
`;

/**
 * Tier 1 promotion progress payload returned alongside each company.
 *
 * Mirrors the backend ``BootstrapProgressBasic`` type. Drives the
 * BUILDING-zero-state UI (banner + readiness gating) without needing
 * a second round-trip on app load.
 */
export interface BootstrapProgress {
  /** Phase: 'new' | 'building' | 'ready'. */
  phase: string | null;
  /** Promoted-eligible-products count. */
  cursor: number | null;
  /** Total eligible products at install commit. */
  total: number | null;
  /** cursor / total as a 0.0..1.0 float; 0.0 when total=0. */
  ratio: number | null;
  /** int(ratio * 100); 0 when total=0. */
  percentage: number | null;
  /** True iff phase === 'ready'. */
  isReady: boolean | null;
}

/**
 * Type definitions for the bootstrap query response
 */
export interface BootstrapCompany {
  id: string;
  companyName: string;
  bootstrapProgress: BootstrapProgress | null;
}

export interface BootstrapOnboardingState {
  isNew: boolean;
  currentStep: number;
  completedSteps: number[];
  billingPlan: string;
}

export interface BootstrapStorePermission {
  storeId: string;
  storeDomain: string;
  requiresReapproval: boolean;
  requiredScopes: string[];
}

export interface BootstrapShopifyPermissionStatus {
  status: string;
  message: string;
  requiresReapproval: boolean;
  stores: BootstrapStorePermission[];
}

export interface AppBootstrapData {
  companies: BootstrapCompany[];
  selectedCompany: BootstrapCompany | null;
  onboardingState: BootstrapOnboardingState | null;
  subscriptionValid: boolean | null;
  shopifyPermissionStatus: BootstrapShopifyPermissionStatus | null;
  currencyCode: string | null;
  defaultSafetyStockWeeks: number | null;
  fallbackSupplierDeliveryTime: number | null;
  warningThreshold: number | null;
  preferredMetric: string | null;
}

export interface GetAppBootstrapResponse {
  getAppBootstrap: AppBootstrapData;
}
